import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../../lib/supabaseClient';

interface Job {
  id: string;
  service_category: string;
  service_subcategory: string;
  status: string;
  scheduled_date: string;
  customer_address: string;
  provider_name: string;
  provider_phone: string;
  provider_latitude: number;
  provider_longitude: number;
  customer_latitude: number;
  customer_longitude: number;
  created_at: string;
  estimated_price: number;
}

export default function ActiveJobs() {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState<Job[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchActiveJobs();
    
    // Subscribe to job updates
    const subscription = supabase
      .channel('active_jobs_changes')
      .on('postgres_changes', 
        { event: '*', schema: 'public', table: 'jobs' }, 
        () => {
          fetchActiveJobs();
        }
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const fetchActiveJobs = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // First, get the jobs
      const { data: jobsData, error: jobsError } = await supabase
        .from('jobs')
        .select('*')
        .eq('customer_id', user.id)
        .in('status', ['pending', 'accepted', 'in_progress', 'en_route'])
        .order('created_at', { ascending: false });

      if (jobsError) throw jobsError;

      // Then, get provider details for jobs that have a provider assigned
      const jobsWithProviders = await Promise.all(
        (jobsData || []).map(async (job: any) => {
          if (job.provider_id) {
            // Get provider profile from users table
            const { data: userData } = await supabase
              .from('users')
              .select('full_name, phone')
              .eq('id', job.provider_id)
              .single();

            return {
              ...job,
              provider_name: userData?.full_name || 'Assigning provider...',
              provider_phone: userData?.phone || ''
            };
          }
          
          return {
            ...job,
            provider_name: 'Assigning provider...',
            provider_phone: ''
          };
        })
      );

      setJobs(jobsWithProviders);
    } catch (error) {
      console.error('Error fetching active jobs:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'accepted': return 'bg-blue-100 text-blue-800';
      case 'en_route': return 'bg-purple-100 text-purple-800';
      case 'in_progress': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'pending': return 'Finding Provider';
      case 'accepted': return 'Provider Assigned';
      case 'en_route': return 'On The Way';
      case 'in_progress': return 'In Progress';
      default: return status;
    }
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <i className="ri-loader-4-line text-4xl text-blue-600 animate-spin"></i>
      </div>
    );
  }

  if (jobs.length === 0) {
    return (
      <div className="bg-white rounded-xl shadow-sm p-12 text-center">
        <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <i className="ri-calendar-check-line text-4xl text-gray-400"></i>
        </div>
        <h3 className="text-xl font-bold text-gray-900 mb-2">No Active Jobs</h3>
        <p className="text-gray-600 mb-6">You don't have any active bookings at the moment</p>
        <button
          onClick={() => navigate('/service-request')}
          className="px-6 py-3 bg-gradient-to-r from-[#FF6A00] to-[#FF8533] text-white rounded-lg hover:shadow-lg transition-all whitespace-nowrap cursor-pointer font-semibold"
        >
          <i className="ri-add-line mr-2"></i>
          Book a Service
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {jobs.map((job) => (
        <div key={job.id} className="bg-white rounded-xl shadow-sm p-6 hover:shadow-md transition-shadow">
          <div className="flex items-start justify-between mb-4">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <h3 className="text-xl font-bold text-gray-900">{job.service_subcategory}</h3>
                <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(job.status)}`}>
                  {getStatusText(job.status)}
                </span>
              </div>
              <p className="text-gray-600 text-sm">{job.service_category}</p>
            </div>
            <button
              onClick={() => navigate(`/job-tracking/${job.id}`)}
              className="px-4 py-2 bg-[#0B3C5D] text-white rounded-lg hover:bg-[#082A42] transition-colors whitespace-nowrap cursor-pointer font-semibold"
            >
              View Details
            </button>
          </div>

          <div className="grid md:grid-cols-2 gap-4 mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                <i className="ri-map-pin-line text-blue-600"></i>
              </div>
              <div>
                <p className="text-xs text-gray-500">Location</p>
                <p className="font-semibold text-gray-900">{job.customer_address}</p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                <i className="ri-calendar-line text-green-600"></i>
              </div>
              <div>
                <p className="text-xs text-gray-500">Scheduled</p>
                <p className="font-semibold text-gray-900">
                  {job.scheduled_date 
                    ? new Date(job.scheduled_date).toLocaleString('en-GB', {
                        day: 'numeric',
                        month: 'short',
                        hour: '2-digit',
                        minute: '2-digit'
                      })
                    : 'ASAP'}
                </p>
              </div>
            </div>
          </div>

          {job.status !== 'pending' && (
            <div className="border-t pt-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-gradient-to-br from-[#FF6A00] to-[#FF8533] rounded-full flex items-center justify-center">
                    <i className="ri-user-line text-white text-xl"></i>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Service Provider</p>
                    <p className="font-bold text-gray-900">{job.provider_name}</p>
                    {job.provider_phone && (
                      <p className="text-sm text-gray-600">{job.provider_phone}</p>
                    )}
                  </div>
                </div>

                <div className="flex gap-2">
                  {job.status === 'in_progress' && (
                    <button
                      onClick={() => navigate(`/job-tracking/${job.id}?action=message`)}
                      className="w-10 h-10 bg-blue-100 text-blue-600 rounded-lg hover:bg-blue-200 transition-colors flex items-center justify-center cursor-pointer"
                      title="Message Provider"
                    >
                      <i className="ri-message-3-line text-xl"></i>
                    </button>
                  )}
                  {(job.status === 'en_route' || job.status === 'in_progress') && (
                    <button
                      onClick={() => navigate(`/job-tracking/${job.id}?action=track`)}
                      className="w-10 h-10 bg-green-100 text-green-600 rounded-lg hover:bg-green-200 transition-colors flex items-center justify-center cursor-pointer"
                      title="Track Location"
                    >
                      <i className="ri-map-pin-line text-xl"></i>
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
